import React, { useMemo } from "react";
import { CircularProgress } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import type { Option } from "@components/FormItem/FormItemFieldRenderer";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import type { SequenceView } from "@services/drybulkCargoOperationsApi";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useAssignEquipment, useEquipmentOptions } from "../hooks/useEquipment";
import { useErrorToast } from "../hooks/useErrorToast";
import { useGatedOpen } from "../hooks/usePollingGate";
import { optionLabel } from "../utils/optionLabel";
import { problemMessage, problemStatus } from "../utils/problemDetail";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.execution.equipment`;

const FIELDS = {
  LOADER: "loaderCode",
  HOPPER: "hopperCode",
  TRANSPORTER: "transporterCompanyCode",
} as const;

const equipmentSchema = (t: (key: string, options?: Record<string, unknown>) => string) => {
  const mandatory = (labelKey: string) =>
    z
      .string()
      .trim()
      .min(1, t("form-validation.mandatory", { label: t(`${L}.fields.${labelKey}`) }));
  return z.object({
    loaderCode: mandatory("loader"),
    hopperCode: mandatory("hopper"),
    transporterCompanyCode: mandatory("transporter"),
  });
};

type EquipmentFormData = z.infer<ReturnType<typeof equipmentSchema>>;

const withSaved = (options: Option[], saved: string | null | undefined): Option[] =>
  saved && !options.some((option) => option.value === saved)
    ? [...options, { label: saved, value: saved }]
    : options;
const EquipmentPanel: React.FC<{
  operationId: string;
  sequence: SequenceView;
  onClose: () => void;
}> = ({ operationId, sequence, onClose }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  useGatedOpen(true);
  const options = useEquipmentOptions(operationId, true);
  const assign = useAssignEquipment(operationId);
  useErrorToast(options.isError, t(`${L}.options-error`));
  const schema = useMemo(() => equipmentSchema(t), [t]);
  const form = useForm<EquipmentFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      loaderCode: sequence.loaderCode ?? "",
      hopperCode: sequence.hopperCode ?? "",
      transporterCompanyCode: sequence.transporterCompanyCode ?? "",
    },
  });

  const loaderOptions = useMemo<Option[]>(
    () =>
      withSaved(
        (options.data?.loaders ?? []).map((loader) => ({
          label: optionLabel(loader.loaderCode, loader.loaderName),
          value: loader.loaderCode,
        })),
        sequence.loaderCode,
      ),
    [options.data, sequence.loaderCode],
  );
  const hopperOptions = useMemo<Option[]>(
    () =>
      withSaved(
        (options.data?.hoppers ?? []).map((hopper) => ({
          label: optionLabel(hopper.hopperCode, hopper.hopperName),
          value: hopper.hopperCode,
        })),
        sequence.hopperCode,
      ),
    [options.data, sequence.hopperCode],
  );
  const transporterOptions = useMemo<Option[]>(
    () =>
      withSaved(
        (options.data?.transporters ?? []).map((transporter) => ({
          label: optionLabel(transporter.companyCode, transporter.companyName),
          value: transporter.companyCode,
        })),
        sequence.transporterCompanyCode,
      ),
    [options.data, sequence.transporterCompanyCode],
  );

  const onValid = async (values: EquipmentFormData) => {
    try {
      await assign.mutateAsync({ sequenceId: sequence.id, request: values });
      onClose();
    } catch (error) {
      const message = problemMessage(error);
      if (problemStatus(error) === 409) {
        notify({
          type: "error",
          title: t(`${L}.refused-title`),
          description: message ?? t(`${L}.save-failed`),
        });
        onClose();
      } else {
        notify({
          type: "error",
          title: t("common.error"),
          description: message ?? t(`${L}.save-failed`),
        });
      }
    }
  };

  return (
    <SideDrawer
      isOpen
      title={t(`${L}.title`, { number: sequence.sequenceNo })}
      onClose={onClose}
      onSubmit={form.handleSubmit(onValid)}
      isSubmitting={assign.isPending}
      isSubmitDisabled={options.isLoading || options.isError}
      submitLabel={t(`${L}.save`)}
      cancelLabel={t(`${L}.cancel`)}
      width={520}
    >
      <div className="w-full flex flex-col gap-2 px-6 py-2" data-testid="equipment-panel">
        {options.isLoading && <CircularProgress aria-label={t(`${L}.loading`)} />}
        <FormProvider {...form}>
          <FormItem<EquipmentFormData, "loaderCode">
            required
            name={FIELDS.LOADER}
            type="autocomplete"
            label={t(`${L}.fields.loader`)}
            testId="equipment-loader"
            options={loaderOptions}
          />
          <FormItem<EquipmentFormData, "hopperCode">
            required
            name={FIELDS.HOPPER}
            type="autocomplete"
            label={t(`${L}.fields.hopper`)}
            testId="equipment-hopper"
            options={hopperOptions}
          />
          <FormItem<EquipmentFormData, "transporterCompanyCode">
            required
            name={FIELDS.TRANSPORTER}
            type="autocomplete"
            label={t(`${L}.fields.transporter`)}
            testId="equipment-transporter"
            options={transporterOptions}
          />
        </FormProvider>
      </div>
    </SideDrawer>
  );
};

export default EquipmentPanel;
